import React, { useRef, useState } from 'react';
import PresignedAvatar from '../common/PresignedAvatar';
import MessageMentionContent from '../common/MessageMentionContent';
import AttachmentGroupBar from './AttachmentGroupBar';
import CompactRichEditor from '../common/CompactRichEditor';
import ConfirmModal from '../common/ConfirmModal';
import { CloseIcon } from './SidebarIcons';
import { getMessageWorkflowVisual, mergeReactionsWithWorkflowCategory } from '../../utils/reactionConfig';
import { normalizeMessageContentHtml, isRichTextMessageContent } from '../../utils/fileUtils';
import { htmlIsMeaningful } from '../../utils/threadCommentUtils';

const resolveReactions = ({ reactions, workflowByCategory }) =>
  mergeReactionsWithWorkflowCategory(reactions, workflowByCategory);

const getBubbleSectionClasses = (reactions, isOwn) => {
  const workflowVisual = getMessageWorkflowVisual(reactions);
  if (workflowVisual) {
    return workflowVisual.isClosed
      ? 'bg-green-50 dark:bg-green-950/40 text-gray-900 dark:text-gray-100 border border-green-200 dark:border-green-800/60'
      : 'bg-yellow-50 dark:bg-yellow-950/35 text-gray-900 dark:text-gray-100 border border-yellow-200 dark:border-yellow-800/50';
  }
  return isOwn
    ? 'bg-primary-600 text-white'
    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700';
};

const isBubbleTextOnLight = (reactions, isOwn) => {
  return !isOwn || Boolean(getMessageWorkflowVisual(reactions));
};

const hasMeaningfulContent = (contentHtml, content) => {
  const raw = (contentHtml || content || '').trim();
  if (!raw) return false;
  if (raw === '<p><br></p>' || raw === '<p></p>') return false;
  return true;
};

export const formatPreviewMessageTime = (timestamp) => {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24));

  const formatTimeOnly = (dateObj) => dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (diffDays === 0) return formatTimeOnly(date);
  if (diffDays === 1) return `Yesterday ${formatTimeOnly(date)}`;

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(date.getDate()).padStart(2, '0');
  return `${day}-${months[date.getMonth()]}-${date.getFullYear()} ${formatTimeOnly(date)}`;
};

/**
 * Compact chat-style message bubble for previews (Today's Tasks, workflows assigned, etc.).
 */
const ChatMessageBubblePreview = ({
  contentHtml,
  content,
  attachments = [],
  attachmentGroupName = '',
  linkPreview,
  reactions,
  workflowByCategory,
  workflowTitle,
  senderName,
  senderProfilePicture,
  createdAt,
  isOwn = false,
  alignLeft = false,
  showAvatar = true,
  compact = true,
  onViewImage,
  onDownload,
  onClick,
  onNavigate,
  onRemove,
  removing = false,
  isEdited = false,
  onSaveEdit,
  onDelete,
}) => {
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const editQuillRef = useRef(null);

  const canManage = isOwn && Boolean(onSaveEdit || onDelete);
  const startEdit = (event) => {
    event?.stopPropagation?.();
    if (!onSaveEdit || editing) return;
    setEditDraft(contentHtml || content || '');
    setEditing(true);
  };
  const cancelEdit = (event) => {
    event?.stopPropagation?.();
    if (savingEdit) return;
    setEditing(false);
    setEditDraft('');
  };
  const saveEdit = async (event) => {
    event?.stopPropagation?.();
    if (!onSaveEdit || savingEdit) return;
    const editor = editQuillRef.current?.getEditor?.();
    const html = editor ? editor.root.innerHTML : editDraft;
    if (!htmlIsMeaningful(html)) return;
    setSavingEdit(true);
    try {
      await onSaveEdit(html);
      setEditing(false);
      setEditDraft('');
    } finally {
      setSavingEdit(false);
    }
  };
  const confirmDeleteComment = async () => {
    if (!onDelete || deleting) return;
    setDeleting(true);
    try {
      await onDelete();
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const effectiveOwn = alignLeft ? false : isOwn;
  const resolvedReactions = resolveReactions({ reactions, workflowByCategory });
  const workflowVisual = getMessageWorkflowVisual(resolvedReactions);
  const watermarkLabel = workflowVisual
    ? (workflowVisual.typeLabel || workflowVisual.watermarkLabel || 'Workflow')
    : '';
  const bubbleClasses = getBubbleSectionClasses(resolvedReactions, effectiveOwn);
  const textOnLight = isBubbleTextOnLight(resolvedReactions, effectiveOwn);

  const rawHtml = (contentHtml?.trim() || '').trim();
  const plainInput = (content || '').trim();
  const htmlSource = isRichTextMessageContent(rawHtml)
    ? rawHtml
    : (isRichTextMessageContent(plainInput) ? plainInput : '');
  const html = normalizeMessageContentHtml(htmlSource);
  const hasHtml = Boolean(htmlSource);
  // Mobile (and other non-Quill) sends plaintext in `content` / `contentHtml`.
  const plain = hasHtml ? '' : (plainInput || rawHtml);
  const hasContent = hasMeaningfulContent(htmlSource, plain);
  const attachmentList = Array.isArray(attachments) ? attachments : [];
  const hasAttachments = attachmentList.length > 0;
  const hasLinkPreview = Boolean(linkPreview?.url);

  if (!editing && !hasContent && !hasAttachments && !hasLinkPreview) {
    return <p className="text-xs text-gray-500 dark:text-gray-400">No message content.</p>;
  }

  const textSizeClass = compact ? 'text-xs' : 'text-sm';
  const paddingClass = compact ? 'px-3 py-2' : 'px-3.5 py-2.5';

  const formattedTime = formatPreviewMessageTime(createdAt);
  const isClickable = Boolean(onClick) || (canManage && Boolean(onSaveEdit) && !editing);

  const handleRichContentClick = (event) => {
    if (!onViewImage) return;
    const img = event.target?.closest?.('img');
    if (!img) return;
    event.stopPropagation();
    const fileUrl = img.getAttribute('data-file-key')
      || img.dataset?.fileKey
      || img.getAttribute('src')
      || '';
    if (!fileUrl) return;
    onViewImage([{
      fileUrl,
      fileName: img.getAttribute('alt') || 'image',
    }], 0);
  };

  return (
    <div
      className={`relative flex gap-2 items-start min-w-0 group/chat-preview ${!alignLeft && effectiveOwn ? 'flex-row-reverse' : ''} ${
        isClickable ? 'cursor-pointer' : ''
      }`}
      onClick={isClickable ? (e) => {
        if (onClick) {
          onClick(e);
          return;
        }
        startEdit(e);
      } : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (onClick) {
            onClick(e);
            return;
          }
          startEdit(e);
        }
      } : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(e);
          }}
          disabled={removing}
          className={`absolute z-20 p-1 rounded-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-800 disabled:opacity-50 transition-opacity ${
            alignLeft || !effectiveOwn ? 'top-0 right-0' : 'top-0 left-0'
          } ${
            removing
              ? 'opacity-100'
              : 'opacity-0 pointer-events-none group-hover/chat-preview:opacity-100 group-hover/chat-preview:pointer-events-auto focus:opacity-100 focus:pointer-events-auto'
          }`}
          title="Remove chat from task"
          aria-label="Remove chat from task"
        >
          <CloseIcon className="w-3.5 h-3.5" />
        </button>
      )}
      {showAvatar && (
        <PresignedAvatar
          fileKey={senderProfilePicture}
          name={senderName}
          initials={senderName ? senderName.substring(0, 2).toUpperCase() : 'U'}
          className="w-7 h-7 rounded-full flex-shrink-0 object-cover"
        />
      )}
      <div className={`min-w-0 flex-1 ${!alignLeft && effectiveOwn ? 'flex flex-col items-end' : ''} max-w-[95%]`}>
        {(senderName || formattedTime || onNavigate) && (
          <div className={`flex items-center gap-1.5 mb-0.5 min-w-0 ${!alignLeft && effectiveOwn ? 'justify-end' : ''}`}>
            <div className={`flex items-baseline gap-1.5 flex-wrap min-w-0 flex-1 ${onRemove ? 'pr-5' : ''}`}>
              {senderName && (
                <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300">
                  {senderName}
                </span>
              )}
              {formattedTime && (
                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                  {formattedTime}
                  {isEdited ? ' · edited' : ''}
                </span>
              )}
              {canManage && !editing && (
                <span className="inline-flex items-center gap-1.5 flex-shrink-0">
                  {onSaveEdit && (
                    <button
                      type="button"
                      onClick={startEdit}
                      className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400"
                    >
                      Edit
                    </button>
                  )}
                  {onSaveEdit && onDelete && (
                    <span className="text-[10px] text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(true);
                      }}
                      className="text-[10px] font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400"
                    >
                      Delete
                    </button>
                  )}
                </span>
              )}
            </div>
            {onNavigate && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate(e);
                }}
                className="flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Go to message in chat"
                aria-label="Go to message in chat"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div
          className={`relative overflow-x-hidden overflow-y-visible rounded-xl shadow-sm break-words ${paddingClass} ${textSizeClass} ${bubbleClasses} ${
            isClickable ? 'group-hover/chat-preview:ring-1 group-hover/chat-preview:ring-primary-300/70 dark:group-hover/chat-preview:ring-primary-600/50 transition-shadow' : ''
          }`}
        >
          <div className={`relative ${workflowVisual ? 'z-[1]' : ''} space-y-2`}>
            {editing ? (
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
                  <CompactRichEditor
                    key="comment-edit"
                    quillRef={editQuillRef}
                    value={editDraft}
                    onChange={setEditDraft}
                    placeholder="Edit your comment…"
                    minHeight={80}
                    maxHeight={180}
                    instanceClass="comment-bubble-edit-quill"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={savingEdit || !htmlIsMeaningful(editDraft)}
                    className="px-2.5 py-1 text-[11px] font-semibold rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {savingEdit ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={savingEdit}
                    className="px-2.5 py-1 text-[11px] font-semibold rounded-md bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : hasContent && (
              hasHtml ? (
                <div
                  onClick={onViewImage ? handleRichContentClick : undefined}
                  className={onViewImage ? '[&_img]:cursor-pointer [&_img]:hover:opacity-90 [&_img]:transition-opacity' : ''}
                >
                  <MessageMentionContent
                    isRichText
                    content={htmlSource}
                    normalizedContent={html}
                    mentionOnLight={textOnLight}
                    className="ql-editor-content [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md"
                    style={{
                      fontSize: compact ? '0.75rem' : '0.875rem',
                      lineHeight: '1.5',
                      color: textOnLight ? 'inherit' : 'rgba(255, 255, 255, 0.9)',
                    }}
                  />
                </div>
              ) : (
                <MessageMentionContent
                  content={plain}
                  mentionOnLight={textOnLight}
                  className={`whitespace-pre-wrap break-words leading-snug ${textOnLight ? '' : 'text-white'}`}
                />
              )
            )}
            {hasAttachments && (
              <div onClick={(e) => e.stopPropagation()}>
                <AttachmentGroupBar
                  attachments={attachmentList}
                  groupName={attachmentGroupName || ''}
                  expanded
                  onViewImage={onViewImage}
                  onDownload={onDownload}
                  mode="display"
                />
              </div>
            )}
            {hasLinkPreview && (
              <a
                href={linkPreview.url.replace(/&amp;/g, '&')}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className={`block border rounded-lg overflow-hidden max-w-full transition-colors ${
                  textOnLight
                    ? 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    : 'border-white/20 hover:bg-white/10'
                }`}
              >
                {linkPreview.imageUrl && (
                  <div className="w-full max-h-40 bg-gray-200 dark:bg-gray-700 overflow-hidden">
                    <img
                      src={linkPreview.imageUrl}
                      alt={linkPreview.title || linkPreview.customTitle || 'Link preview'}
                      className="w-full h-full max-h-40 object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div className="p-2">
                  {linkPreview.siteName && (
                    <p className={`text-[10px] font-medium uppercase mb-0.5 ${
                      textOnLight ? 'text-gray-500 dark:text-gray-400' : 'text-white/70'
                    }`}
                    >
                      {linkPreview.siteName}
                    </p>
                  )}
                  {(linkPreview.customTitle || linkPreview.title) && (
                    <p className={`text-xs font-semibold line-clamp-2 ${
                      textOnLight ? 'text-gray-900 dark:text-white' : 'text-white'
                    }`}
                    >
                      {linkPreview.customTitle || linkPreview.title}
                    </p>
                  )}
                </div>
              </a>
            )}
          </div>
          {workflowVisual && watermarkLabel ? (
            <div className="relative z-[1] flex items-center justify-end pt-1 pointer-events-none select-none" aria-hidden="true">
              <span
                className="text-sm font-bold uppercase tracking-wider opacity-[0.18] dark:opacity-[0.12] text-gray-800 dark:text-gray-200 whitespace-nowrap leading-none truncate max-w-full"
                title={watermarkLabel}
              >
                {watermarkLabel}
              </span>
            </div>
          ) : null}
        </div>
      </div>
      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => {
          if (!deleting) setConfirmDelete(false);
        }}
        onConfirm={confirmDeleteComment}
        title="Delete comment"
        message="Delete this comment? This cannot be undone."
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        type="danger"
      />
    </div>
  );
};

export default ChatMessageBubblePreview;
